import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Tooltip,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useInvitedUserOtpVerification } from '../hooks/useInvitedUserOtpVerification';
import {
  isPhoneNumberFormatValid,
  isPhoneNumberValid as validatePhoneNumberFormat,
} from '../../../Profile.helpers';
import { acceptInvite } from '../../../../services/newUsers';
import { Neutrals, Primary } from '../../../../core-components/theme/color-palette/colors';
import SpriteIcon from '../../../../core-components/assets/Icon';
import { loginUsingAuthToken } from '../../../../services/Login';
import useUserStore from '../../../../store/UserStore';
import { decodeToken } from '../../../../utils/jwt.utils';
import { addCachedLoginEntry } from '../../../../utilities/cachedLoginEntries.utils';
import { loginData } from '../../../../Interfaces/loginInterfaces';

interface InviteUserFormProps {
  inviteToken: string;
  initialData: {
    userId: string;
    name: string;
    email: string;
  };
}

interface InviteUserFormData {
  phoneNumber: string;
  password: string;
  confirmPassword: string;
}

const inviteUserFormSchema = yup.object().shape({
  phoneNumber: yup
    .string()
    .required('Phone number is required')
    .test('is-valid-phone', 'Please enter a valid 10-digit phone number', value => {
      return validatePhoneNumberFormat(value);
    }),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
});

export default function InviteUserForm({ inviteToken, initialData }: InviteUserFormProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const {
    setToken,
    setLanguage,
    setSideBar,
    setUserId,
    setUserName,
    setLandingPage,
    setAllowAction,
  } = useUserStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<InviteUserFormData>({
    resolver: yupResolver(inviteUserFormSchema),
  });

  const watchedPhoneNumber = watch('phoneNumber') || '';

  const {
    otpGenerated,
    otpValue,
    isVerifying,
    originalVerifiedPhoneNumber,
    isPhoneNumberVerified,
    resendTimer,
    setOtpValue,
    handleGenerateOtp,
    handleEditPhoneNumber,
    handleResendOtp,
    handleVerifyOtp,
    resetOtpState,
    generateOtpMutation,
    verifyOtpMutation,
  } = useInvitedUserOtpVerification({
    userId: initialData.userId,
    phoneNumber: watchedPhoneNumber,
  });

  const handleClickShowPassword = () => setShowPassword(show => !show);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword(show => !show);

  const formatTimer = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const storeCurrentUserInfo = (data: loginData) => {
    const { role, userId, userName } = decodeToken(data?.token);
    localStorage.setItem("token", data?.token || "");
    localStorage.setItem("userid", userId); // currently used in birection controle components

    setToken(data?.token);
    setLanguage("English");
    setSideBar(data.layoutInfo.sideBar);
    setUserId(userId);
    setUserName(userName);
    setLandingPage(data.layoutInfo.landingPage);

    if (role === "Operator Group") {
      setAllowAction(false);
    } else {
      setAllowAction(true);
    }
  };

  const handleUserLogin = async (token: string) => {
    try {
      // Login using the new token
      const loginData: loginData | undefined = await loginUsingAuthToken(token);
      
      if (loginData?.token) {
        storeCurrentUserInfo(loginData);
        const { userId } = decodeToken(loginData.token);
        
        // Add cached login entry
        addCachedLoginEntry(userId, loginData?.token || "");
        
        // Navigate to landing page
        navigate(loginData.layoutInfo.landingPage, { replace: true });
        toast.success('Account created successfully. Welcome to DigitalPaani!');
      } else {
        toast.error('Failed to complete login. Please try logging in manually.');
        navigate('/Login', { replace: true });
      }
    } catch (error: any) {
      console.error('Error during automatic login:', error);
      toast.error('Failed to complete login. Please try logging in manually.');
      navigate('/Login', { replace: true });
    }
  };

  const onSubmit = async (formData: InviteUserFormData) => {
    if (!isPhoneNumberVerified) {
      toast.error('Please verify your phone number before submitting');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!isPhoneNumberFormatValid(formData.phoneNumber)) {
      toast.error('Please enter a valid phone number');
      return;
    }

    if (isPhoneNumberVerified && originalVerifiedPhoneNumber && formData.phoneNumber !== originalVerifiedPhoneNumber) {
      toast.error('Please verify the new phone number');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await acceptInvite({
        userId: initialData.userId,
        phoneNumber: formData.phoneNumber,
        password: formData.password,
      });

      if (response?.status === true && response?.data?.token) {
        await handleUserLogin(response.data.token);
      } else {
        toast.error(response?.message || 'Failed to create account. Please try again.');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to create account. Please try again.');
      navigate('/Login', { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        overflow: { xs: 'auto', md: 'hidden' },
        py: { xs: 2, sm: 3, md: 5 },
        px: { xs: 1, sm: 2 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: '600px', md: '1200px' },
          maxHeight: { xs: 'none', md: 'calc(100vh - 64px - 32px)' },
          bgcolor: 'white',
          borderRadius: { xs: 0, sm: 2 },
          p: { xs: 2, sm: 3, md: 5 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ width: '100%', maxWidth: '1100px', height: '100%', mb: { xs: 0, md: 3 } }}>
          {/* Left Section - Logo and Welcome Message */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                textAlign: 'center',
                px: { xs: 1, sm: 2, md: 4 },
                py: { xs: 2, md: 0 },
              }}
            >
              <Box 
                sx={{ 
                  mb: { xs: 2, sm: 3, md: 4 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  '& svg': {
                    width: '100%',
                    height: 'auto',
                    maxWidth: { xs: '200px', sm: '280px', md: '350px' },
                  },
                }}
              >
                <SpriteIcon 
                  id='doctor_paani' 
                  width={350}
                  height={337}
                  fill='none'
                />
              </Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  mb: { xs: 1, md: 2 }, 
                  fontWeight: 600, 
                  color: Primary[700],
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' },
                }}
              >
                You've been invited to join
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: { xs: 0.5, md: 1 }, 
                  fontWeight: 700, 
                  color: Primary[900],
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                }}
              >
                DigitalPaani
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: Neutrals[600],
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  px: { xs: 2, md: 0 },
                }}
              >
                Complete your account setup to get started
              </Typography>
            </Box>
          </Grid>

          {/* Right Section - Form */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                px: { xs: 1, sm: 2, md: 4 },
                py: { xs: 2, md: 0 },
              }}
            >
              <Typography  
                sx={{ 
                  mb: { xs: 2, sm: 3, md: 4 }, 
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '30px' }, 
                  width: '100%', 
                  textAlign: 'center' 
                }}
              >
                Create your account
              </Typography>

              <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ width: '100%', maxWidth: { xs: '100%', sm: '450px', md: '500px' } }}>
                <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                {/* Name Field - Disabled */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={initialData.name}
                    disabled
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Email Field - Disabled */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={initialData.email}
                    disabled
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Phone Number Field */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    variant="outlined"
                    {...register('phoneNumber')}
                    // keep it non-editable while OTP is being entered, but allow a pencil icon to re-enable editing
                    InputProps={{
                      readOnly: otpGenerated,
                      endAdornment: otpGenerated ? (
                        <InputAdornment position="end">
                          <Tooltip title="Edit Phone Number" arrow placement="top">
                            <IconButton
                              aria-label="Edit Phone Number"
                              edge="end"
                              size="small"
                              onClick={handleEditPhoneNumber}
                              sx={{ color: Primary[700] }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ) : undefined,
                    }}
                    type="number"
                    onKeyDown={(evt) => ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault()}
                    error={
                      Boolean(
                        errors.phoneNumber ||
                        (watchedPhoneNumber && !validatePhoneNumberFormat(watchedPhoneNumber)) ||
                        (watchedPhoneNumber && isPhoneNumberVerified && originalVerifiedPhoneNumber && watchedPhoneNumber !== originalVerifiedPhoneNumber)
                      )
                    }
                    helperText={
                      errors.phoneNumber?.message ||
                      (watchedPhoneNumber && !validatePhoneNumberFormat(watchedPhoneNumber) ? 'Please enter a valid phone number' :
                      watchedPhoneNumber && isPhoneNumberVerified && originalVerifiedPhoneNumber && watchedPhoneNumber !== originalVerifiedPhoneNumber
                        ? 'Please verify the new number.'
                        : watchedPhoneNumber && isPhoneNumberVerified
                          ? 'Phone number verified'
                          : watchedPhoneNumber && !otpGenerated
                            ? 'Please verify your phone number'
                            : watchedPhoneNumber && otpGenerated
                              ? 'OTP sent on WhatsApp. Please enter the OTP to verify.'
                              : '')
                    }
                    InputLabelProps={{ shrink: !!watchedPhoneNumber }}
                    onChange={e => {
                      const newNumber = e.target.value;
                      setValue('phoneNumber', newNumber, { shouldValidate: true });
                      // If phone number changed from verified number, reset OTP state and verification
                      if (isPhoneNumberVerified && originalVerifiedPhoneNumber && newNumber !== originalVerifiedPhoneNumber) {
                        resetOtpState();
                      }
                    }}
                  />
                </Grid>

                {/* Generate OTP Button */}
                {(!isPhoneNumberVerified || (isPhoneNumberVerified && originalVerifiedPhoneNumber && watchedPhoneNumber !== originalVerifiedPhoneNumber)) && !otpGenerated && (
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={handleGenerateOtp}
                      disabled={generateOtpMutation.isPending || !validatePhoneNumberFormat(watchedPhoneNumber)}
                      fullWidth
                      sx={{
                        backgroundColor: (generateOtpMutation.isPending || !validatePhoneNumberFormat(watchedPhoneNumber)) ? Neutrals[300] : Primary[700],
                        color: '#fff',
                        py: { xs: 1.25, md: 1.5 },
                        fontSize: { xs: '0.875rem', md: '1rem' },
                      }}
                    >
                      {generateOtpMutation.isPending ? (
                        <>
                          <CircularProgress size={20} sx={{ color: 'inherit', marginRight: '10px' }} />
                          Generating...
                        </>
                      ) : (
                        'Generate OTP'
                      )}
                    </Button>
                  </Grid>
                )}

                {/* OTP Verification Section */}
                {otpGenerated && (
                  <>
                    <Grid item xs={12}>
                      <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Enter OTP"
                            variant="outlined"
                            value={otpValue}
                            type="number"
                            onKeyDown={(evt) => ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault()}
                            onChange={e => setOtpValue(e.target.value)}
                            inputProps={{ maxLength: 6 }}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Button
                            variant="contained"
                            onClick={handleVerifyOtp}
                            disabled={isVerifying || verifyOtpMutation.isPending || !otpValue}
                            fullWidth
                            sx={{
                              height: { xs: '40px', sm: '40px' },
                              backgroundColor: Primary[700],
                              color: '#fff',
                            }}
                          >
                            {isVerifying || verifyOtpMutation.isPending ? (
                              <CircularProgress size={20} sx={{ color: 'inherit' }} />
                            ) : (
                              'Verify & Save'
                            )}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'end' }, alignItems: 'center', height: "auto"}}>
                      {generateOtpMutation.isPending ? (
                        <>
                          <CircularProgress size={16} sx={{ color: 'inherit', marginRight: '8px' }} />
                          <Typography 
                          variant="body2" 
                          style={{ 
                            color: Neutrals[600],
                            fontSize: '12px', 
                            textTransform: 'none',
                          }}
                        > Resending...
                        </Typography>
                        </>
                      ) : resendTimer > 0 ? (
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: Neutrals[600],
                            fontSize: { xs: '11px', sm: '12px' }, 
                            textTransform: 'none',
                          }}
                        >
                          Resend OTP in {formatTimer(resendTimer)}
                        </Typography>
                      ) : (
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: Primary[700],
                            fontSize: { xs: '11px', sm: '12px' }, 
                            textTransform: 'none', 
                            cursor: 'pointer' 
                          }} 
                          onClick={handleResendOtp}
                        >
                          Resend OTP
                        </Typography>
                      )}
                    </Grid>
                  </>
                )}

                {/* Password Field */}
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined" error={Boolean(errors.password)}>
                    <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel>
                    <OutlinedInput
                      id="outlined-adornment-password"
                      type={showPassword ? 'text' : 'password'}
                      {...register('password')}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? (
                              <VisibilityOff
                                style={{
                                  height: '18px',
                                  width: '18px',
                                  color: '#999999',
                                }}
                              />
                            ) : (
                              <Visibility
                                style={{
                                  height: '18px',
                                  width: '18px',
                                  color: '#999999',
                                }}
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                      label="Password"
                    />
                    {errors.password && (
                      <FormHelperText error>{errors.password.message}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Confirm Password Field */}
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined" error={Boolean(errors.confirmPassword)}>
                    <InputLabel htmlFor="outlined-adornment-confirm-password">Confirm Password</InputLabel>
                    <OutlinedInput
                      id="outlined-adornment-confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...register('confirmPassword')}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowConfirmPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff
                                style={{
                                  height: '18px',
                                  width: '18px',
                                  color: '#999999',
                                }}
                              />
                            ) : (
                              <Visibility
                                style={{
                                  height: '18px',
                                  width: '18px',
                                  color: '#999999',
                                }}
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                      label="Confirm Password"
                    />
                    {errors.confirmPassword && (
                      <FormHelperText error>{errors.confirmPassword.message}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={Boolean(isSubmitting || !isPhoneNumberVerified || (isPhoneNumberVerified && originalVerifiedPhoneNumber && watchedPhoneNumber !== originalVerifiedPhoneNumber))}
                    sx={{
                      backgroundColor: Primary[700],
                      color: '#fff',
                      py: { xs: 1.25, md: 1.5 },
                      mt: { xs: 1, md: 2 },
                      fontSize: { xs: '0.875rem', md: '1rem' },
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <CircularProgress size={20} sx={{ color: 'inherit', marginRight: '10px' }} />
                        Creating Account...
                      </>
                    ) : (
                      'Save & Continue'
                    )}
                  </Button>
                </Grid>
              </Grid>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
